import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { User, RegisterData } from '../types/User';
import { apiClient } from '../services/apiClient';

interface AuthContextData {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (data: RegisterData) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const token = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('user');

        console.log('🔄 Verificando autenticação:', {
          hasToken: !!token,
          hasStoredUser: !!storedUser,
          tokenPreview: token ? `${token.substring(0, 20)}...` : null
        });

        if (token && storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
          console.log('✅ Usuário autenticado:', userData);
        } else {
          console.log('❌ Usuário não autenticado');
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Erro ao verificar autenticação:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiClient.auth.login(email, password);
      
      if (response.token && response.user) {
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      console.log('🚀 Iniciando registro no AuthContext:', data);
      const response = await apiClient.auth.register(data);
      console.log('✅ Resposta do registro:', response);
      
      if (response.token && response.user) {
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        setIsAuthenticated(true);
      }
    } catch (error: any) {
      console.error('❌ Erro detalhado no AuthContext:', {
        error,
        message: error?.message,
        response: error?.response,
        responseData: error?.response?.data,
        status: error?.response?.status
      });
      
      // Propaga o erro original com mais detalhes
      if (error.response?.data?.message) {
        console.log('📝 Erro da API:', error.response.data.message);
        throw new Error(error.response.data.message);
      } else if (error.response?.status === 400) {
        console.log('📝 Erro 400:', error.response.data);
        throw new Error(error.response.data);
      } else {
        console.log('📝 Erro genérico:', error);
        throw error;
      }
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await apiClient.auth.forgotPassword(email);
    } catch (error) {
      console.error('❌ Erro ao solicitar recuperação de senha:', error);
      throw error;
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      await apiClient.auth.resetPassword(token, password);
    } catch (error) {
      console.error('❌ Erro ao redefinir senha:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (updatedUser: User) => {
    try {
      // Atualizar o estado
      setUser(updatedUser);
      
      // Atualizar no localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      console.log('✅ Usuário atualizado:', updatedUser);
    } catch (error) {
      console.error('❌ Erro ao atualizar usuário:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isAuthenticated,
      login,
      logout,
      register,
      forgotPassword,
      resetPassword,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
} 